import { useCallback, useEffect, useState } from "react";
import type { Subscription } from "api";
import { subscriptionsApi } from "api";
import { isForbidden, isUnauthenticated } from "utils/error.utils";
import type { LoadStatus } from "./useProfile";

type UseSubscriptions = {
  subscriptions: Subscription[];
  status: LoadStatus;
  /**
   * Fetch again in the background. Usage moves whenever anyone in the company
   * calls a tool — following a shipment does — so a screen showing those
   * figures has to be able to ask again without a page reload.
   */
  reload: () => void;
};

/**
 * Subscriptions belong to the company, so every colleague sees the same list.
 * An empty list is a legitimate answer — it drives the "nothing running yet"
 * state rather than an error.
 */
export const useSubscriptions = (onSessionLost: () => void): UseSubscriptions => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [status, setStatus] = useState<LoadStatus>("loading");

  const load = useCallback(
    (background: boolean) => {
      let cancelled = false;

      subscriptionsApi
        .list()
        .then(({ data }) => {
          if (cancelled) return;
          setSubscriptions(data.data);
          setStatus("ready");
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          if (isUnauthenticated(error)) {
            onSessionLost();
            setStatus("unauthorized");
            return;
          }
          // A refresh that fails leaves the last good figures alone: a blip is
          // not worth replacing readable numbers with an error.
          if (background) return;

          setStatus(isForbidden(error) ? "unverified" : "failed");
        });

      return () => {
        cancelled = true;
      };
    },
    [onSessionLost]
  );

  useEffect(() => load(false), [load]);

  const reload = useCallback(() => {
    load(true);
  }, [load]);

  return { subscriptions, status, reload };
};
